// LadderBlock.tsx — v12.10.15 (#1641): "Offer the keypad" (typed digits always accepted) + "If no key is pressed" Move on | fail route.
// LadderBlock.tsx — v12.9.26c (#967): ladder tooltip says Ask cap.
// LadderBlock.tsx — v12.9.26b (#987): CONFIRM_BUILTIN table (Confirm card built-in lines) generated from PlatformPhrases.
// LadderBlock.tsx — v12.9.26: FIELD_LABEL memberId Spanish re-synced to PlatformPhrases (q92, Check-LadderDefaults).
// LadderBlock.tsx — v12.9.24 (#914): generated tables re-synced to Santos's reviewed Spanish in PlatformPhrases.cs (Check-LadderDefaults).
// LadderBlock.tsx - v12.9.2 (#691): an empty rung box now SHOWS the platform sentence it will fall back to, mirrored from LadderRungs.DefaultPrompt and gated against drift.
// components/designer/LadderBlock.tsx - v1.0 - v12.7.3 (#647 / #640)
//
// "IF NOT CAPTURED" - THE ESCALATION LADDER, ON THE STEP CARD.
//
// Tushar, 2026-09-23: *"when we exhaust retry cap, then these options should kick on some variation as a last
// try to collect the details"* and *"that would be meaningful turn to capture response in a different way
// instead of trying same method again and again."*
//
// WHY THE SWITCHES ARE TYPE-DEPENDENT. His instruction: *"it will only show available switches depending on
// the field type."* A keypad cannot take a name and a name cannot be split into a year - offering either is an
// invitation to configure something that will never run. This is the same defect the `Unsure` dropdown had,
// where two of its four options had never differed from Default on any step; the fix there was to LABEL the
// dead options, and the fix here is not to render them at all.
//
// WHY EVERY SWITCH CARRIES A SENTENCE. Also his: *"don't forget for these switches, we will need that preview
// message we locked earlier no hard coded messages to truly support multiple languages."* Turning a rung on
// makes the agent SAY something. Without an editable sentence per rung, per language, Split ask and DTMF would
// ship three new hardcoded English literals - which is #627 and #640 all over again, inside the very screen
// built to stop that happening.
//
// THE CHIPS ARE FLATTENED, NEVER NESTED - his word. Two names on a Name step; Year / Month / Day / Keypad on a
// date step. Switch a rung off and its chips go with it.
//
// SOURCE OF TRUTH: Providers/LadderRungs.cs. The availability rules below MIRROR AvailableFor/PartsForStep and
// must be changed with them. They are duplicated rather than served because this screen has to re-render as the
// author types a field name, and a round-trip per keystroke is not worth one switch row.
import { useState } from 'react';
import { PromptEditor } from '../PromptEditor';
import type { TokenGroup } from '../aiagent/TokenPicker';
import { HelpTooltip } from '../aiagent/HelpTooltip';   // v12.8.0 (#660)

export type Rung = 'spell' | 'split' | 'dtmf';

/** Mirrors LadderRungs.AvailableFor. Order matters: it is the order the rungs RUN in, keypad last. */
export function availableRungs(fieldType: string | undefined): Rung[] {
  switch ((fieldType ?? '').toLowerCase()) {
    case 'name':                 return ['spell'];
    // v12.7.3 (#647): email gets no rung - the spell assembler is a NAME assembler and cannot express
    // "at" or "dot". Mirrors LadderRungs.AvailableFor; see its comment for why none is better than one.
    case 'email':                return [];
    case 'date':
    case 'birthdate':            return ['split', 'dtmf'];
    case 'zip':
    case 'phone':
    case 'number':               return ['dtmf'];
    default:                     return [];   // text, choice, time, unknown: nothing a rung can do
  }
}

/** Mirrors LadderRungs.PartsForStep - the STEP decides, not only the type. See UI-LOCKED 12.7.3 section 6c. */
export function splitParts(fieldType: string | undefined, isBirthdate: boolean, storedKeys: string[]): string[] {
  if (storedKeys.length > 1) return storedKeys;
  switch ((fieldType ?? '').toLowerCase()) {
    case 'birthdate':            return ['year', 'month', 'day'];
    case 'date':                 return isBirthdate ? ['year', 'month', 'day'] : ['month', 'day'];
    default:                     return [];
  }
}

const RUNG_LABEL: Record<Rung, string> = {
  // "Ask to Spell", his wording - shortened from "Ask them to spell it" on 2026-09-23.
  spell: 'Ask to Spell',
  split: 'Split ask',
  dtmf:  'Offer the keypad',   // v12.10.15 (#1641, Identity prototype v1.3 LOCKED): typed digits are always accepted while we ask
};

const RUNG_HELP: Record<Rung, string> = {
  spell: 'After the ordinary re-asks are spent, ask the caller to spell it letter by letter.',
  split: 'After the ordinary re-asks are spent, ask for it one piece at a time instead of all at once.',
  dtmf:  'Offer the keypad when not captured: after the spoken re-asks, ask the caller to key it in. Digits typed while we ask are always accepted, with this switch on or off.',
};

// ── v12.9.2 (#691): WHAT THE AGENT WILL ACTUALLY SAY WHEN THIS BOX IS EMPTY. ──────────────────────────
//
// The box has said "leave blank to use the platform default" since #640 and has never shown the default.
// Tushar, 2026-09-25, having cleared the box: *"when i remove this i don't see any default in light gray colour
// in the box so how i know please spell first name letter by letter?"* A default nobody can read is a default
// nobody trusts, which is how two builds were spent on a sentence he thought he had configured.
//
// MIRRORED FROM `LadderRungs.DefaultPrompt` - the engine is the source of truth and this is a second copy, so
// `tools\Check-RungPromptKeys.mjs` compares the two and fails the build when they drift. A comment asking a
// human to keep two copies in step is not a mechanism (#412's lesson).
// ── v12.9.5 (#737): THE SCREEN MUST NAME THE FIELD THE WAY THE CALLER HEARS IT. ──────────────────────
//
// His screenshot, 2026-09-25: the box read *"Can you spell **Subject > First name** for me..."* and the caller
// heard *"...spell **the first name**..."*. The engine builds that word with `HumanFieldName(key)` - the LAST
// segment only, lower-cased because it is spoken mid-sentence - and this mirror was handed `labelOf(key)`,
// the full breadcrumb built for a chip label.
//
// A preview that does not match the call is worse than no preview: it is how #725 hid for a whole release,
// with the screen promising one sentence and the call speaking another. Mirrors the engine's FieldSpoken map
// exactly; anything not in it falls through to the same prettifier, lower-cased the same way.
// ── v12.9.18 (#855): GENERATED FROM THE ENGINE'S PHRASE ROWS. DO NOT HAND-EDIT. ────────────────────────
//
// Tushar found this on his 12.9.17 smoke test: the Identity node's Spanish tab showed the ENGLISH rung
// sentence under a "Template default" badge, and PREVIEW - ES faithfully previewed the English.
//
// The cause was two English-only copies in this file - `rungPlatformDefault` and `FIELD_SPOKEN` - neither of
// which took a language. The old comment on FIELD_SPOKEN read "Mirrors the engine's FieldSpoken map exactly".
// **That map no longer exists**; 12.9.18 deleted it and made the engine read `field.label.*` rows. A comment
// asserting two things are in step is not a mechanism, which is #412's lesson and now #855's.
//
// These tables are GENERATED from `PlatformPhrases.cs` and `tools\Check-LadderDefaults.mjs` FAILS THE BUILD
// when they drift from it. The real fix is T2 reading the rows over an API; that is blocked on the
// platform-phrase store (#845/#848), so this is a known interim WITH A GUARD rather than a fourth copy.
// ══ v12.9.21 (#863): THE SEED PROMPT FOR A NEW IDENTITY STEP, GENERATED FROM THE ENGINE ═══════════════
// identStepPrompt() in WorkflowModalSnapshotPage held seven English literals and took no language, so a step
// added on the Spanish tab was seeded with an English question. The sentences now live in PlatformPhrases as
// `identSeed.*` - a seed becomes the tenant's prompt the moment it is saved, and from then on a caller HEARS
// it, so it is platform speech and belongs beside the rest of it.
// GENERATED - `tools\Check-LadderDefaults_v1.1.mjs` fails the build on drift.
const IDENT_SEED: Record<string, Record<string, string>> = {
  "name": {
    "en": "Please tell me the patient's full name.",
    "es": "Por favor, dígame el nombre completo del paciente."
  },
  "dob": {
    "en": "What is the patient's date of birth?",
    "es": "¿Cuál es la fecha de nacimiento del paciente?"
  },
  "phone": {
    "en": "What is the best phone number to reach you?",
    "es": "¿Cuál es el mejor número para comunicarnos con usted?"
  },
  "memberId": {
    "en": "What is your member or subscriber ID?",
    "es": "¿Cuál es su número de identificación o suscriptor?"
  },
  "zip": {
    "en": "What is your ZIP code?",
    "es": "¿Cuál es su código postal?"
  },
  "email": {
    "en": "What is your email address?",
    "es": "¿Cuál es su correo electrónico?"
  },
  "default": {
    "en": "Please provide your {0}.",
    "es": "Por favor, indíqueme su {0}."
  }
};

/** The seed question for a new identity step, in the tab's language. */
export function identSeedPrompt(key: string, name: string, language = 'en'): string {
  const k = (key || '').toLowerCase();
  const lang = (language || 'en').split('-')[0].toLowerCase();
  const pick = (t: string) => IDENT_SEED[t]?.[lang] ?? IDENT_SEED[t]?.en ?? '';
  let token = 'default';
  if (k.includes('name')) token = 'name';
  else if (k.includes('dob') || k.includes('birth')) token = 'dob';
  else if (k.includes('phone')) token = 'phone';
  else if (k.includes('member') || k.includes('subscriber')) token = 'memberId';
  else if (k.includes('zip') || k.includes('postal')) token = 'zip';
  else if (k.includes('email')) token = 'email';
  const s = pick(token);
  return token === 'default' ? s.replace('{0}', (name || key).toLowerCase()) : s;
}

// ══ v12.9.21 (#860): THE RE-ASK SENTENCE, GENERATED FROM THE ENGINE ═══════════════════════════════════
// WorkflowModalSnapshotPage held a hardcoded ENGLISH ladder of assembled re-ask sentences and took no
// language, so the Spanish tab showed "Please give it one at a time." That is #412 returning: T2 keeping
// its own copy of text the engine owns. Nothing needed translating - every `reask.*` row already carried
// Spanish; T2 simply never asked.
//
// TWO DRIFTS WERE FOUND WHEN THIS WAS GENERATED RATHER THAN COPIED. T2's literals read "Please give it THE
// day and month." and "Please give it THE area code first."; the engine composes "day and month" and "area
// code first" with no article. T2 was previewing sentences the engine would never speak, in ENGLISH, which
// is the same defect #855 fixed for the rungs and the reason a copy is never acceptable however careful.
//
// GENERATED from `PlatformPhrases.cs` - `tools\Check-LadderDefaults_v1.1.mjs` fails the build on drift.
const REASK_DEFAULTS: Record<string, Record<string, string>> = {
  "yearOnly": {
    "en": "And what year?",
    "es": "¿Y en qué año?"
  },
  "giveItAs": {
    "en": "Please give it {0}.",
    "es": "Por favor dígalo {0}."
  },
  "hint.one-at-a-time": {
    "en": "one at a time",
    "es": "uno a la vez"
  },
  "hint.year-first": {
    "en": "year first",
    "es": "el año primero"
  },
  "hint.day-and-month": {
    "en": "day and month",
    "es": "el día y el mes"
  },
  "hint.digit-by-digit": {
    "en": "digit by digit",
    "es": "dígito por dígito"
  },
  "hint.area-code-first": {
    "en": "area code first",
    "es": "el código de área primero"
  },
  "hint.letter-by-letter": {
    "en": "letter by letter",
    "es": "letra por letra"
  },
  "hint.just-the-year": {
    "en": "just the year",
    "es": "solo el año"
  }
};

// Which hint the engine picks, mirroring SnapshotCallInterpreter.SplitAskHintToken. The MAPPING lives here;
// the WORDS never do.
function reaskHintToken(fieldType: string | undefined, isBirthdate: boolean, storeCount: number): string | null {
  if (storeCount > 1) return 'one-at-a-time';
  switch ((fieldType ?? '').toLowerCase()) {
    case 'birthdate': return 'year-first';
    case 'date':      return isBirthdate ? 'year-first' : 'day-and-month';
    case 'zip':       return 'digit-by-digit';
    case 'number':    return 'digit-by-digit';
    case 'phone':     return 'area-code-first';
    case 'name':      return 'letter-by-letter';
    default:          return null;
  }
}

/** The sentence the ENGINE will speak on a type-failure retry, in the tab's language. Shown, never saved. */
export function reaskPlatformDefault(
  fieldType: string | undefined, isBirthdate: boolean, storeCount: number, language = 'en',
): string | null {
  const token = reaskHintToken(fieldType, isBirthdate, storeCount);
  if (!token) return null;
  const lang = (language || 'en').split('-')[0].toLowerCase();
  const pick = (k: string) => REASK_DEFAULTS[k]?.[lang] ?? REASK_DEFAULTS[k]?.en ?? '';
  const frame = pick('giveItAs');
  const hint  = pick('hint.' + token);
  if (!frame || !hint) return null;
  return frame.replace('{0}', hint);
}

const RUNG_DEFAULTS: Record<string, Record<string, string>> = {
  "that": {
    "en": "that",
    "es": "eso"
  },
  "spell": {
    "en": "Please spell {0}, letter by letter.",
    "es": "Por favor, deletree {0}, letra por letra."
  },
  "split.year": {
    "en": "What year were you born?",
    "es": "¿En qué año nació usted?"
  },
  "split.month": {
    "en": "What month?",
    "es": "¿En qué mes?"
  },
  "split.day": {
    "en": "And what day of the month?",
    "es": "¿Y qué día del mes?"
  },
  "split.date": {
    "en": "Which day would you like to come in?",
    "es": "¿Qué día le gustaría venir?"
  },
  "split.time": {
    "en": "And what time?",
    "es": "¿Y a qué hora?"
  },
  "split.default": {
    "en": "Please tell me {0}.",
    "es": "Por favor, dígame {0}."
  },
  "dtmf.known": {
    "en": "Please enter {0} using your keypad. That's {1} digits.",
    "es": "Por favor, presione {0} en su teclado. Son {1} dígitos."
  },
  "dtmf.known.one": {
    "en": "Please enter {0} using your keypad. That's {1} digit.",
    "es": "Por favor, presione {0} en su teclado. Es {1} dígito."
  },
  "dtmf.unknown": {
    "en": "Please enter {0} using your keypad, then press the pound key.",
    "es": "Por favor, presione {0} en su teclado y luego presione la tecla de número."
  }
};

const FIELD_LABEL: Record<string, Record<string, string>> = {
  "firstName": {
    "en": "first name",
    "es": "nombre"
  },
  "lastName": {
    "en": "last name",
    "es": "apellido"
  },
  "name": {
    "en": "full name",
    "es": "nombre completo"
  },
  "dob": {
    "en": "date of birth",
    "es": "fecha de nacimiento"
  },
  "dateOfBirth": {
    "en": "date of birth",
    "es": "fecha de nacimiento"
  },
  "zip": {
    "en": "ZIP code",
    "es": "código postal"
  },
  "phone": {
    "en": "phone number",
    "es": "número de teléfono"
  },
  "date": {
    "en": "date",
    "es": "fecha"
  },
  "time": {
    "en": "time",
    "es": "hora"
  },
  "email": {
    "en": "email address",
    "es": "correo electrónico"
  },
  "address1": {
    "en": "address",
    "es": "dirección"
  },
  "address2": {
    "en": "address line 2",
    "es": "segunda línea de la dirección"
  },
  "memberId": {
    "en": "member ID",
    "es": "número de identificación del seguro"
  },
  "newDate": {
    "en": "new date",
    "es": "nueva fecha"
  },
  "newTime": {
    "en": "new time",
    "es": "nueva hora"
  },
  "reason": {
    "en": "reason",
    "es": "motivo"
  },
  "provider": {
    "en": "provider",
    "es": "doctor"
  },
  "location": {
    "en": "location",
    "es": "lugar"
  }
};

/** The row's text for this language, falling back to English only where that is what the engine does too. */
function row(table: Record<string, Record<string, string>>, key: string, language: string): string | undefined {
  const lang = (language || 'en').split(/[-_]/)[0].toLowerCase();
  const r = table[key];
  if (!r) return undefined;
  const mine = r[lang];
  if (mine && mine.trim()) return mine;
  const en = r.en;
  return en && en.trim() ? en : undefined;
}

const fmt = (s: string, ...args: (string | number)[]) =>
  s.replace(/\{(\d)\}/g, (_m, i) => String(args[Number(i)] ?? ''));

export function spokenFieldName(key: string, language = 'en'): string {
  const last = (key || '').split('.').pop() ?? '';
  if (!last.trim()) return last;
  const hit = row(FIELD_LABEL, last, language) ?? row(FIELD_LABEL, last.toLowerCase(), language);
  if (hit) return hit;
  return last.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').trim().toLowerCase();
}

function rungPlatformDefault(rung: string, part: string, fieldLabel: string, fieldType: string | undefined, language = 'en'): string {
  const what = (fieldLabel || '').trim() || row(RUNG_DEFAULTS, 'that', language) || 'that';
  if (rung === 'spell') return fmt(row(RUNG_DEFAULTS, 'spell', language) ?? '', what);
  if (rung === 'split') {
    const seg = (part || '').toLowerCase().split('.').pop() ?? '';
    const named = row(RUNG_DEFAULTS, `split.${seg}`, language);
    if (named) return named;
    return fmt(row(RUNG_DEFAULTS, 'split.default', language) ?? '', what);
  }
  // dtmf - the sentence has to tell the caller how the turn ends, so it differs by whether the length is known.
  const len = fieldType === 'zip' ? 5 : fieldType === 'phone' ? 10 : fieldType === 'birthdate' || fieldType === 'date' ? 8 : 0;
  return len > 0
    // dtmf.known.one (a one-digit answer) is carried in the table so the gate can prove it matches the engine;
    // no field type here has a length of one, so the editor never shows it.
    ? fmt(row(RUNG_DEFAULTS, 'dtmf.known', language) ?? '', what, len)
    : fmt(row(RUNG_DEFAULTS, 'dtmf.unknown', language) ?? '', what);
}

export interface LadderValue {
  askToSpell?: boolean;
  splitAsk?: boolean;
  dtmf?: boolean;
  /** rungPrompts["split:year"]["en"] = "What year were you born?" */
  rungPrompts?: Record<string, Record<string, string>>;
  /**
   * v12.9.5 (#738): the same shape and the same keys, but authored in the WORKFLOW TEMPLATE rather than by
   * this tenant. It is what an empty box falls back to and what the badge calls "Template default".
   * Read-only here on purpose - a tenant edits rungPrompts; the template is changed in T4.
   */
  rungDefaults?: Record<string, Record<string, string>>;
  /** v12.10.15 (#1641): no key pressed after the keypad is offered - 'moveon' (default) or 'fail' (the node's fail route). */
  keypadNoKey?: string;
}

export function LadderBlock(props: {
  value: LadderValue;
  onChange: (patch: LadderValue) => void;
  fieldType: string | undefined;
  isBirthdate: boolean;
  storedKeys: string[];
  /** Pretty label for a stored key, so a chip reads "Patient > Last name" and not "subject.lastName". */
  labelOf: (key: string) => string;
  language: string;
  groups: TokenGroup[];
  known: string[];
  disabled?: boolean;
}) {
  const { value, onChange, fieldType, isBirthdate, storedKeys, labelOf, language, groups, known, disabled } = props;
  const rungs = availableRungs(fieldType);
  const [sel, setSel] = useState<string | null>(null);

  // Nothing this field type can do differently - say so rather than render an empty row. A step with no field
  // yet says nothing at all, because the author has not told us what it captures.
  if (rungs.length === 0) {
    if (!fieldType) return null;
    return (
      <div className="rq-ladder">
        <span className="rq-ladlab">If not captured <span className="ai-agent-panel tenant-help-scope"><HelpTooltip sections={[['What it does', 'The recovery ladder. When the plain asks have run out, the agent tries a DIFFERENT method rather than repeating itself - spell it, split the question, or take it on the keypad. Only the methods this field type can actually use are shown.'], ['When it fires', 'After the node\u2019s Ask cap is reached, not before. Each switch you turn on is one more attempt: with an Ask cap of 3 and both Split ask and Keypad on, the caller gets five turns in total.'], ['Example', 'A date of birth: three plain asks, then \u201cWhat year were you born?\u201d, then \u201cPlease enter it using your keypad.\u201d If none of them lands, the miss is recorded and the call CONTINUES (#643).'], ['Every switch is a sentence', 'Turning one on gives it its own editable line, per language, with a preview - because a switch that changes what the caller hears has to let you write what they hear.']]} /></span></span>
        {/* v12.8.0 (#660): ": " and a space. These are two adjacent spans and the screen read
            "If not capturedNothing to try differently..." as one word. */}
        <span className="rq-ladnone">: nothing to try differently on a {fieldType} field &mdash; it cannot be spelled, split or keyed in.</span>
      </div>
    );
  }

  const on = (r: Rung) => r === 'spell' ? value.askToSpell === true
                        : r === 'split' ? value.splitAsk === true
                        : value.dtmf === true;
  const toggle = (r: Rung) => {
    if (disabled) return;
    const next: LadderValue = r === 'spell' ? { askToSpell: !on(r) } : r === 'split' ? { splitAsk: !on(r) } : { dtmf: !on(r) };
    onChange(next);
    setSel(null);   // the chips below just changed; a stale selection would edit a sentence nobody can see
  };

  // The flattened chip row: every rung that is ON contributes its own chips, in rung order.
  const chips: { id: string; label: string }[] = [];
  for (const r of rungs) {
    if (!on(r)) continue;
    if (r === 'spell') {
      // One chip per field the step stores - a two-name step must be able to say WHICH name it wants (#573).
      const keys = storedKeys.length ? storedKeys : [''];
      for (const k of keys) chips.push({ id: 'spell:' + k, label: labelOf(k) || 'Spell' });
    } else if (r === 'split') {
      for (const p of splitParts(fieldType, isBirthdate, storedKeys))
        chips.push({ id: 'split:' + p, label: p.includes('.') ? labelOf(p) : p.charAt(0).toUpperCase() + p.slice(1) });
    } else {
      chips.push({ id: 'dtmf:' + (storedKeys[0] ?? ''), label: 'Keypad' });
    }
  }
  const active = sel && chips.some(c => c.id === sel) ? sel : (chips[0]?.id ?? null);
  const sentence = active ? (value.rungPrompts?.[active]?.[language] ?? '') : '';

  return (
    <div className="rq-ladder">
      <div className="rq-ladhead">
        <span className="rq-ladlab">If not captured <span className="ai-agent-panel tenant-help-scope"><HelpTooltip sections={[['What it does', 'The recovery ladder. When the plain asks have run out, the agent tries a DIFFERENT method rather than repeating itself - spell it, split the question, or take it on the keypad. Only the methods this field type can actually use are shown.'], ['When it fires', 'After the node\u2019s Ask cap is reached, not before. Each switch you turn on is one more attempt: with an Ask cap of 3 and both Split ask and Keypad on, the caller gets five turns in total.'], ['Example', 'A date of birth: three plain asks, then \u201cWhat year were you born?\u201d, then \u201cPlease enter it using your keypad.\u201d If none of them lands, the miss is recorded and the call CONTINUES (#643).'], ['Every switch is a sentence', 'Turning one on gives it its own editable line, per language, with a preview - because a switch that changes what the caller hears has to let you write what they hear.']]} /></span></span>
        <span className="rq-ladsw">
          {rungs.map(r => (
            <span key={r} className="rq-ladtog" title={RUNG_HELP[r]}>
              <span className="rq-ladtog-txt">{RUNG_LABEL[r]}</span>
              <button type="button" role="switch" aria-checked={on(r)} aria-label={RUNG_LABEL[r]} disabled={disabled}
                className={'rq-sw' + (on(r) ? ' on' : '')} onClick={() => toggle(r)}><span className="rq-sw-thumb" aria-hidden="true" /></button>
            </span>
          ))}
        </span>
      </div>

      {/* ── v12.10.15 (#1641, LOCKED S122): the keypad, in two parts - typed digits are ALWAYS taken while we ask; the switch
          above only OFFERS the keypad after the spoken re-asks; and a caller who presses nothing never waits or hangs up. ── */}
      {rungs.includes('dtmf') && (
        <div className="rq-ladkeypad">
          <span className="rq-ladkeypad-note">Digits typed on the keypad while we ask are always accepted.</span>
          {on('dtmf') && (
            <span className="rq-ladkeypad-nokey">
              <span className="rq-ladtog-txt">If no key is pressed</span>
              <select className="rq-in" value={value.keypadNoKey === 'fail' ? 'fail' : 'moveon'} disabled={disabled}
                onChange={e => onChange({ keypadNoKey: e.target.value })}>
                <option value="moveon">Move on - leave it empty, flag it for staff</option>
                <option value="fail">Go to the step&rsquo;s fail route</option>
              </select>
            </span>
          )}
        </div>
      )}

      {chips.length > 0 && (
        <>
          <div className="rq-ladchips">
            {chips.map(c => (
              <button key={c.id} type="button" className={'rq-ladchip' + (c.id === active ? ' on' : '')}
                onClick={() => setSel(c.id)}>{c.label}</button>
            ))}
          </div>
          {active && (
            <PromptEditor
              value={sentence}
              rows={2}
              language={language}
              noTokens          /* v12.8.0 (#660): his call - no tokens on a rung sentence */
              /* v12.9.4 (#731): the default now lives IN the box - shown when empty, editable on focus, and
                 badged top right - instead of in a note under the preview. The placeholder text below is
                 what a box with no platform default still shows. */
              /* ── v12.9.5 (#738): THE TEMPLATE'S SENTENCE, THEN OURS. ──────────────────────────
                 The default shown in the box is what the WORKFLOW TEMPLATE defines, so clearing the box
                 returns to the template rather than to a literal in the engine, and a new platform shows
                 what its own template says. The reconstruction below is only reached by a template that
                 defines nothing - which also retires #737's root cause rather than patching it, since
                 there is nothing left to rebuild from a breadcrumb in the usual case. */
              platformDefault={
                value.rungDefaults?.[active]?.[language]
                ?? value.rungDefaults?.[active]?.en
                ?? rungPlatformDefault(
                     active.split(':')[0],
                     active.slice(active.indexOf(':') + 1),
                     /* v12.9.5 (#737): named as the CALLER hears it, not as a chip label. */
                     spokenFieldName(active.slice(active.indexOf(':') + 1), language) || active.slice(active.indexOf(':') + 1),
                     fieldType,
                     /* v12.9.18 (#855): the language of the active tab, so the Spanish tab shows Spanish. */
                     language)}
              placeholder="What the agent says for this one…"
              groups={groups}
              known={known}
              onChange={v => {
                const rp: Record<string, Record<string, string>> = { ...(value.rungPrompts ?? {}) };
                rp[active] = { ...(rp[active] ?? {}), [language]: v };
                onChange({ rungPrompts: rp });
              }} />
          )}
          {/* v12.9.2 (#691) shipped this as a NOTE under the preview; v12.9.4 (#731) moves it INTO the box -
              the sentence as editable grey text, the badge top right - because a default you cannot see in the
              control you are editing is a default you cannot check. That is how #725 stayed hidden. */}
        </>
      )}
    </div>
  );
}

// v12.9.26 (#987): GENERATED from PlatformPhrases confirm.correction / confirm.cantConfirm - the Confirm card shows the
// line a blank box falls back to, in the tab language. Guarded by tools/Check-LadderDefaults_v1.2.mjs. Do not hand-edit.
export const CONFIRM_BUILTIN: Record<string, Record<string, string>> = {
  "correction": {
    "en": "Sorry about that. Please tell me what isn't correct, and what it should be.",
    "es": "Disculpe. Por favor, dígame qué dato no es correcto y cuál debería ser."
  },
  "cantConfirm": {
    "en": "Sorry, I am having trouble collecting the correct information.",
    "es": "Disculpe, estoy teniendo dificultades para obtener la información correcta."
  }
};
